import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import usePermissions from '../../auth/usePermissions';

import RoomGrid from '../../components/Projects/Library/RoomGrid';
import ItemGrid from '../../components/Projects/Library/ItemGrid';
import AddSectionForm from '../../components/Projects/Library/AddSectionForm';
import AddItemForm from '../../components/Projects/Library/AddItemForm';
import AnimatedDropdown from '../../components/Projects/Library/AnimatedDropdown';
import ItemFolioModal from '../../components/Projects/Library/ItemFolioModal';

import '../../component-styles/LibrarySection.css';
import API_BASE from '../../utils/api.js';
import { sortRoomsForDesignFlow } from '../../utils/designWorkflow.js';

function getApiBase() {
  return API_BASE;
}

export default function ProjectLibrarySection() {
  const outlet = useOutletContext?.() || {};
  const projectId = outlet.projectId || '';
  const project = outlet.project || {};

  const { permissions } = usePermissions();
  const canEdit = permissions.createProductsServices !== false;

  const userId = localStorage.getItem('userId');

  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(true);

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState('alphabetical');

  const [showSectionForm, setShowSectionForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedToDelete, setSelectedToDelete] = useState([]);

  const [itemSelectionMode, setItemSelectionMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [moveToSectionId, setMoveToSectionId] = useState('');

  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchLibrary();
  }, [userId, projectId]);

  const cacheKey = `library-cache:${userId || 'anon'}:${projectId || 'all'}`;

  async function fetchLibrary() {
    if (!userId) return;

    try {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed?.sections) && Array.isArray(parsed?.items)) {
            setSections(parsed.sections);
            setItems(parsed.items);
          }
        }
      } catch {}

      setLoadingLibrary(true);

      const url = projectId
        ? `${getApiBase()}/api/library/${userId}/${projectId}`
        : `${getApiBase()}/api/library/${userId}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch library');

      const { sections = [], items = [] } = await res.json();

      const normalizedSections = (sections || [])
        .filter((s) => s.type === 'section')
        .map((s) => ({
          ...s,
          sectionId: s.sectionId || s.sk,
        }));

      const normalizedItems = items || [];

      setSections(normalizedSections);
      setItems(normalizedItems);

      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ sections: normalizedSections, items: normalizedItems, ts: Date.now() })
        );
      } catch {}
    } catch (err) {
      console.error('Error fetching library:', err);
    } finally {
      setLoadingLibrary(false);
    }
  }

  const handleAddNew = () => {
    if (deleteMode) return;
    setShowSectionForm(true);
  };

  const handleSaveSection = async (section) => {
    try {
      setShowSectionForm(false);

      const newSection = {
        sectionId: section.sectionId || `sec_${Date.now()}`,
        name: section.name,
        projectId,
        type: 'section',
        createdAt: new Date().toISOString(),
      };

      setSections((prev) => [newSection, ...prev]);
      setTimeout(fetchLibrary, 600);
    } catch (err) {
      console.error('Error saving new section:', err);
    }
  };

  const toggleDeleteMode = () => {
    setDeleteMode((prev) => !prev);
    setSelectedToDelete([]);
  };

  const toggleRoomSelection = (sectionId) => {
    setSelectedToDelete((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
    );
  };

  const handleDeleteRooms = async () => {
    if (selectedToDelete.length === 0) return;

    try {
      await fetch(`${getApiBase()}/api/library/delete-sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          projectId,
          sectionIds: selectedToDelete,
        }),
      });

      setSections((prev) => prev.filter((sec) => !selectedToDelete.includes(sec.sectionId)));

      setSelectedToDelete([]);
      setDeleteMode(false);
    } catch (err) {
      console.error('Error deleting rooms:', err);
    }
  };

  const toggleItemSelectionMode = () => {
    setItemSelectionMode((prev) => !prev);
    setSelectedItemIds([]);
    setMoveToSectionId('');
  };

  const moveRoomItems = async () => {
    if (!selectedRoom?.sectionId || !moveToSectionId || selectedItemIds.length === 0) return;
    if (moveToSectionId === selectedRoom.sectionId) return;

    try {
      const res = await fetch(`${getApiBase()}/api/library/move-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          projectId,
          itemIds: selectedItemIds,
          targetSectionId: moveToSectionId,
        }),
      });

      if (!res.ok) throw new Error('Failed to move selected items');

      const targetRoom = sections.find((room) => room.sectionId === moveToSectionId);
      setItems((prev) =>
        prev.map((item) =>
          selectedItemIds.includes(item.sk)
            ? {
                ...item,
                sectionId: moveToSectionId,
                room: targetRoom?.name || item.room,
              }
            : item
        )
      );

      setSelectedItemIds([]);
      setMoveToSectionId('');
      setItemSelectionMode(false);
    } catch (err) {
      console.error('Failed to move selected items:', err);
    }
  };

  const handleDeleteItem = (itemId) => {
    setItems((prev) => prev.filter((i) => (i.sk || i.itemId) !== itemId));
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        parsed.items = (parsed.items || []).filter((i) => (i.sk || i.itemId) !== itemId);
        localStorage.setItem(cacheKey, JSON.stringify(parsed));
      }
    } catch {}
  };

  const handleUpdateItem = (updated) => {
    const updatedId = updated.sk || updated.itemId;
    setItems((prev) =>
      prev.map((i) => ((i.sk || i.itemId) === updatedId ? { ...i, ...updated } : i))
    );
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        parsed.items = (parsed.items || []).map((i) =>
          (i.sk || i.itemId) === updatedId ? { ...i, ...updated } : i
        );
        localStorage.setItem(cacheKey, JSON.stringify(parsed));
      }
    } catch {}
  };

  const filteredSections = useMemo(() => {
    const bySearch = sections.filter((room) =>
      room.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return sortRoomsForDesignFlow(bySearch);
  }, [sections, searchQuery]);

  const itemsBySection = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.sectionId || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return map;
  }, [items]);

  const filteredItems = useMemo(() => {
    const list = items.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        item.sectionId === selectedRoom?.sectionId
    );

    if (sortValue === 'category') {
      const priority = { product: 1, service: 2 };
      return list.sort((a, b) => {
        const aType = a.type?.toLowerCase?.() || '';
        const bType = b.type?.toLowerCase?.() || '';
        return (priority[aType] || 99) - (priority[bType] || 99);
      });
    }

    if (sortValue === 'vendor') {
      return list.sort((a, b) => {
        const aVendor = (a.vendorName || a.vendor || '').toLowerCase();
        const bVendor = (b.vendorName || b.vendor || '').toLowerCase();
        if (aVendor !== bVendor) return aVendor.localeCompare(bVendor);
        return (a.name || '').localeCompare(b.name || '');
      });
    }

    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [items, searchQuery, selectedRoom, sortValue]);

  if (loadingLibrary && !sections.length) {
    return (
      <div className="library-page">
        <div className="library-empty-state">
          <div className="library-empty-icon" aria-hidden="true">
            ...
          </div>
          <div className="library-empty-title">Loading library…</div>
          <div className="library-empty-sub">Preparing your sections and items.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="library-page">
      <div className="library-controls">
        <div className="library-controls-row">
          <div className="library-search-box">
            <input
              type="text"
              placeholder={
                project?.projectNickname
                  ? `Search ${project.projectNickname} Library`
                  : 'Search Library'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {!selectedRoom && canEdit && (
            <div className="library-top-buttons">
              <button className="library-delete-button" onClick={toggleDeleteMode}>
                {deleteMode ? 'Cancel Delete' : 'Delete Rooms'}
              </button>

              <button className="library-add-button" onClick={handleAddNew} disabled={deleteMode}>
                + Add New Section
              </button>
            </div>
          )}
        </div>

        {deleteMode && selectedToDelete.length > 0 && (
          <div className="library-delete-bar">
            <span>{selectedToDelete.length} selected</span>
            <button className="library-delete-confirm-btn" onClick={handleDeleteRooms}>
              Delete
            </button>
          </div>
        )}

        {selectedRoom && (
          <div className="library-sort-row">
            <div className="library-sort-wrapper">
              <label>Sort by</label>
              <AnimatedDropdown
                options={[
                  { label: 'Alphabetical', value: 'alphabetical' },
                  { label: 'Category (Products / Services)', value: 'category' },
                  { label: 'Vendor', value: 'vendor' },
                ]}
                value={sortValue}
                onChange={(v) => setSortValue(v)}
                placeholder="Sort..."
              />
            </div>
            {canEdit && (
              <button className="library-secondary-button" onClick={toggleItemSelectionMode}>
                {itemSelectionMode ? 'Cancel Move' : 'Move Items'}
              </button>
            )}
          </div>
        )}

        {selectedRoom && itemSelectionMode && (
          <div className="library-sort-row">
            <div className="library-sort-wrapper">
              <label>Move to</label>
              <select
                className="library-sort-select"
                value={moveToSectionId}
                onChange={(e) => setMoveToSectionId(e.target.value)}
              >
                <option value="">Select room</option>
                {filteredSections
                  .filter((room) => room.sectionId !== selectedRoom.sectionId)
                  .map((room) => (
                    <option key={room.sectionId} value={room.sectionId}>
                      {room.name}
                    </option>
                  ))}
              </select>
            </div>
            <button
              className="library-add-button"
              onClick={moveRoomItems}
              disabled={!moveToSectionId || selectedItemIds.length === 0}
            >
              Move {selectedItemIds.length || ''} Item{selectedItemIds.length === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </div>

      {!selectedRoom ? (
        <RoomGrid
          rooms={filteredSections}
          itemsBySection={itemsBySection}
          onSelectRoom={setSelectedRoom}
          deleteMode={deleteMode}
          selectedToDelete={selectedToDelete}
          onToggleSelect={toggleRoomSelection}
        />
      ) : (
        <ItemGrid
          room={selectedRoom}
          items={filteredItems}
          onBack={() => {
            setSelectedRoom(null);
            setItemSelectionMode(false);
            setSelectedItemIds([]);
            setMoveToSectionId('');
          }}
          onItemClick={setSelectedItem}
          itemSelectionMode={itemSelectionMode}
          selectedItemIds={selectedItemIds}
          onToggleItemSelect={(itemId) =>
            setSelectedItemIds((prev) =>
              prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
            )
          }
        />
      )}

      {showSectionForm && (
        <AddSectionForm
          onClose={() => setShowSectionForm(false)}
          onSave={handleSaveSection}
          projectId={projectId}
        />
      )}

      {editingItem && (
        <AddItemForm
          sectionId={selectedRoom?.sectionId}
          item={editingItem}
          onClose={() => {
            setEditingItem(null);
          }}
          onSave={(saved) => {
            if (editingItem) {
              handleUpdateItem(saved);
            } else {
              setItems((prev) => [...prev, saved]);
              setTimeout(fetchLibrary, 600);
            }
          }}
        />
      )}

      {selectedItem && (
        <ItemFolioModal
          item={selectedItem}
          projectId={projectId}
          onClose={() => setSelectedItem(null)}
          onEdit={canEdit ? (item) => setEditingItem(item) : null}
          onDelete={canEdit ? handleDeleteItem : null}
        />
      )}
    </div>
  );
}
